import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Search, Plus, Eye, X, Pencil, Wand2, Building2, RefreshCw, Archive, Upload, Filter, XCircle, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useFetch, Spinner } from '../lib/useFetch';
import { companiesApi, companiesExportApi } from '../lib/api';
import { toast } from 'sonner';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import AutoProfileDialog from '../components/AutoProfileDialog';
import { DismissCompanyDialog, ReactivateCompanyDialog } from '../components/DismissalDialogs';
import { companyFields } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';

// Filter definitions for profilazione (all boolean SI/NO fields).
const SI_NO_FILTERS = [
  { key: 'somministrazioneHACCP', label: 'Somministrazione HACCP' },
  { key: 'supportoHACCP', label: 'Supporto HACCP' },
  { key: 'rspp_csc', label: 'RSPP CSC' },
  { key: 'cpi', label: 'CPI' },
  { key: 'presenzaPiscine', label: 'Presenza piscine' },
  { key: 'legionella', label: 'Legionella' },
  { key: 'cantieri', label: 'Cantieri' },
];

const emptyFilters = () => ({
  rischio: 'all',           // all | BASSO | MEDIO | ALTO
  macrosettore: 'all',      // dynamic
  medicoCompetente: 'all',  // dynamic + '__valued'
  respPiscine: 'all',       // dynamic + '__valued'
  contratto: 'all',         // all | empty | filled | specific
  sedi: 'all',              // all | 1 | 2 | 3 | 4 | 5+
  si_no: {},                // { fieldKey: 'SI' | 'NO' | 'empty' | 'all' }
});

const AziendePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialSede = params.get('sede') || '';
  const initialMacro = params.get('macrosettore') || 'all';
  const [search, setSearch] = useState(initialSede);
  const [pageSize, setPageSize] = useState('10');
  const [page, setPage] = useState(1);
  const [autoOpen, setAutoOpen] = useState(false);
  const [tab, setTab] = useState('attive');
  const [dismissTarget, setDismissTarget] = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [showFilters, setShowFilters] = useState(initialMacro !== 'all');
  const [f, setF] = useState(() => ({ ...emptyFilters(), macrosettore: initialMacro }));
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await companiesExportApi.downloadXlsx(tab === 'archivio' ? 'dismesse' : 'attive');
      toast.success('Excel scaricato');
    } catch (e) {
      toast.error('Errore durante il download');
    } finally {
      setExporting(false);
    }
  };

  const status = tab === 'archivio' ? 'dismesso' : undefined;
  const { data, loading, reload } = useFetch(() => companiesApi.list({ status }), [tab]);
  const crud = useCrudActions({ api: companiesApi, label: 'azienda', onChange: reload });

  useEffect(() => {
    if (params.get('sede') && params.get('sede') !== search) {
      setSearch(params.get('sede'));
    }
  }, [params, search]);

  // Dynamic filter option pools built from the data
  const dynamicOptions = useMemo(() => {
    const list = data || [];
    const macros = new Set();
    const medici = new Set();
    const respPiscine = new Set();
    const contratti = new Set();
    list.forEach((c) => {
      if (c.macrosettore) macros.add(String(c.macrosettore));
      if (c.medicoCompetente) medici.add(String(c.medicoCompetente));
      if (c.respPiscine) respPiscine.add(String(c.respPiscine));
      if (c.contratto) contratti.add(String(c.contratto));
    });
    return {
      macros: Array.from(macros).sort(),
      medici: Array.from(medici).sort(),
      respPiscine: Array.from(respPiscine).sort(),
      contratti: Array.from(contratti).sort(),
    };
  }, [data]);

  const filtered = useMemo(() => {
    let list = data || [];
    // Text search — matches name, address, piva, cf, email(s)
    if (search) {
      const q = search.toLowerCase().trim();
      list = list.filter((c) => {
        const hay = [
          c.name, c.address, c.sede, c.piva, c.codiceFiscale, c.email,
          Array.isArray(c.emails) ? c.emails.join(' ') : '',
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    // Rischio
    if (f.rischio !== 'all') {
      list = list.filter((c) => (c.rischio || '').toUpperCase() === f.rischio);
    }
    if (f.macrosettore !== 'all') {
      const target = f.macrosettore.toLowerCase();
      if (target === 'non_definito' || target === 'non definito') {
        list = list.filter((c) => !(c.macrosettore || '').trim());
      } else {
        list = list.filter((c) => (c.macrosettore || '').toLowerCase() === target
          // Support click from Dashboard: incoming key may be the initial letter
          // of the ATECO section ("a", "c", …) OR the lowercased full label.
          || (target.length === 1 && (c.macrosettore || '').toLowerCase().startsWith(`${target} - `))
          || (target.length >= 2 && (c.macrosettore || '').toLowerCase().startsWith(target)));
      }
    }
    if (f.medicoCompetente !== 'all') {
      if (f.medicoCompetente === '__valued') {
        list = list.filter((c) => !!(c.medicoCompetente || '').trim());
      } else if (f.medicoCompetente === '__empty') {
        list = list.filter((c) => !(c.medicoCompetente || '').trim());
      } else {
        list = list.filter((c) => (c.medicoCompetente || '') === f.medicoCompetente);
      }
    }
    if (f.respPiscine !== 'all') {
      if (f.respPiscine === '__valued') list = list.filter((c) => !!(c.respPiscine || '').trim());
      else if (f.respPiscine === '__empty') list = list.filter((c) => !(c.respPiscine || '').trim());
      else list = list.filter((c) => (c.respPiscine || '') === f.respPiscine);
    }
    if (f.contratto !== 'all') {
      if (f.contratto === '__empty') list = list.filter((c) => !(c.contratto || '').trim() || c.contratto === '-');
      else if (f.contratto === '__valued') list = list.filter((c) => (c.contratto || '').trim() && c.contratto !== '-');
      else list = list.filter((c) => (c.contratto || '') === f.contratto);
    }
    if (f.sedi !== 'all') {
      list = list.filter((c) => {
        const n = Number(c.sedi || 0);
        if (f.sedi === '5+') return n >= 5;
        return n === Number(f.sedi);
      });
    }
    // SI/NO/vuoto filters
    Object.entries(f.si_no).forEach(([k, val]) => {
      if (!val || val === 'all') return;
      if (val === 'empty') {
        list = list.filter((c) => c[k] == null || c[k] === '' || c[k] === undefined);
      } else {
        list = list.filter((c) => (c[k] || '').toString().toUpperCase() === val);
      }
    });
    return list;
  }, [search, data, f]);

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    ['rischio', 'macrosettore', 'medicoCompetente', 'respPiscine', 'contratto', 'sedi'].forEach((k) => {
      if (f[k] && f[k] !== 'all') n += 1;
    });
    n += Object.values(f.si_no).filter((v) => v && v !== 'all').length;
    return n;
  }, [f]);

  const clearFilters = () => {
    setF(emptyFilters());
    // Also strip the macrosettore query param so a page reload doesn't re-apply it.
    if (params.get('macrosettore')) {
      const p = new URLSearchParams(params);
      p.delete('macrosettore');
      setParams(p, { replace: true });
    }
    setPage(1);
  };

  const size = parseInt(pageSize, 10);
  const totalPages = Math.max(1, Math.ceil(filtered.length / size));
  const paged = filtered.slice((page - 1) * size, page * size);

  if (loading) return <Spinner />;

  const isArchivio = tab === 'archivio';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="aziende-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-gray-800">Elenco aziende</h2>
          <span className="w-4 h-4 rounded-full bg-[#023F80] text-white text-[10px] font-bold flex items-center justify-center">i</span>
          <span className="text-xs text-slate-500" data-testid="aziende-total-count">
            ({filtered.length}{filtered.length !== (data || []).length ? ` / ${(data || []).length}` : ''} totali)
          </span>
        </div>
        {user?.role !== 'client' && !isArchivio && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-aziende-btn"
              className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-bold tracking-wider flex items-center gap-2 disabled:opacity-50"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              ESPORTA AZIENDE
            </button>
            <Link
              to="/aziende/importa"
              data-testid="import-aziende-btn"
              className="ro-hide px-4 py-2 rounded-full bg-white border border-[#023F80] text-[#023F80] hover:bg-[#023F80] hover:text-white text-xs font-bold tracking-wider flex items-center gap-2 transition-colors"
            >
              <Upload size={14} /> IMPORTA EXCEL
            </Link>
            <button
              onClick={() => setAutoOpen(true)}
              title="Profila automaticamente le aziende (con anteprima)"
              data-testid="auto-profile-open-btn"
              className="ro-hide px-4 py-2 rounded-full bg-[#023F80] text-white text-xs font-bold tracking-wider hover:bg-[#022F60] flex items-center gap-2"
            >
              <Wand2 size={14} className="text-[#FFCB05]" /> PROFILA AZIENDE
            </button>
            <button onClick={() => crud.onCreate({ contract: '-' })} className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#FFCB05] flex items-center gap-2">
              <Plus size={14} /> NUOVA AZIENDA
            </button>
          </div>
        )}
      </div>

      {user?.role !== 'client' && (
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }} className="mb-4">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="attive" data-testid="aziende-tab-attive" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
              <Building2 size={14} /> Attive
            </TabsTrigger>
            <TabsTrigger value="archivio" data-testid="aziende-tab-archivio" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
              <Archive size={14} /> Archivio (dismesse)
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Search + Filters toggle */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="aziende-search-input"
            placeholder="Cerca per denominazione, P.IVA, CF, indirizzo, email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 h-10 border-gray-200"
          />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
              <X size={13} className="text-slate-400" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          data-testid="toggle-filters-btn"
          className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 border transition-colors ${showFilters ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
        >
          <Filter size={13} /> Filtri {activeFiltersCount > 0 && <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${showFilters ? 'bg-white/20' : 'bg-[#023F80] text-white'}`}>{activeFiltersCount}</span>}
        </button>
        {activeFiltersCount > 0 && (
          <button
            onClick={clearFilters}
            data-testid="clear-filters-btn"
            className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <XCircle size={13} /> Azzera filtri
          </button>
        )}
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="mb-4 p-4 rounded-xl border border-slate-200 bg-slate-50" data-testid="filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <FilterSelect
              label="Rischio"
              value={f.rischio}
              onChange={(v) => setF({ ...f, rischio: v })}
              options={[
                { value: 'all', label: 'Tutti' },
                { value: 'BASSO', label: 'Basso' },
                { value: 'MEDIO', label: 'Medio' },
                { value: 'ALTO', label: 'Alto' },
              ]}
              testId="filter-rischio"
            />
            <FilterSelect
              label="Macrosettore"
              value={f.macrosettore}
              onChange={(v) => setF({ ...f, macrosettore: v })}
              options={[{ value: 'all', label: 'Tutti' }, ...dynamicOptions.macros.map((m) => ({ value: m, label: m.length > 40 ? m.slice(0, 40) + '…' : m }))]}
              testId="filter-macrosettore"
            />
            <FilterSelect
              label="Medico competente"
              value={f.medicoCompetente}
              onChange={(v) => setF({ ...f, medicoCompetente: v })}
              options={[
                { value: 'all', label: 'Tutti' },
                { value: '__valued', label: '— valorizzato' },
                { value: '__empty', label: '— non valorizzato' },
                ...dynamicOptions.medici.map((m) => ({ value: m, label: m })),
              ]}
              testId="filter-medico"
            />
            <FilterSelect
              label="Resp. Piscine"
              value={f.respPiscine}
              onChange={(v) => setF({ ...f, respPiscine: v })}
              options={[
                { value: 'all', label: 'Tutti' },
                { value: '__valued', label: '— valorizzato' },
                { value: '__empty', label: '— non valorizzato' },
                ...dynamicOptions.respPiscine.map((m) => ({ value: m, label: m })),
              ]}
              testId="filter-resppiscine"
            />
            <FilterSelect
              label="Contratto"
              value={f.contratto}
              onChange={(v) => setF({ ...f, contratto: v })}
              options={[
                { value: 'all', label: 'Tutti' },
                { value: '__empty', label: 'Vuoto' },
                { value: '__valued', label: 'Valorizzato' },
                ...dynamicOptions.contratti.map((m) => ({ value: m, label: m })),
              ]}
              testId="filter-contratto"
            />
            <FilterSelect
              label="Sedi"
              value={f.sedi}
              onChange={(v) => setF({ ...f, sedi: v })}
              options={[
                { value: 'all', label: 'Tutti' },
                { value: '1', label: '1 sede' },
                { value: '2', label: '2 sedi' },
                { value: '3', label: '3 sedi' },
                { value: '4', label: '4 sedi' },
                { value: '5+', label: '5 o più' },
              ]}
              testId="filter-sedi"
            />
            {SI_NO_FILTERS.map((sf) => (
              <FilterSelect
                key={sf.key}
                label={sf.label}
                value={f.si_no[sf.key] || 'all'}
                onChange={(v) => setF({ ...f, si_no: { ...f.si_no, [sf.key]: v } })}
                options={[
                  { value: 'all', label: 'Tutti' },
                  { value: 'SI', label: 'SI' },
                  { value: 'NO', label: 'NO' },
                  { value: 'empty', label: 'Non valorizzato' },
                ]}
                testId={`filter-${sf.key}`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Denominazione</th>
              <th className="text-left bg-gray-100 px-4 py-3">P.IVA / CF</th>
              <th className="text-left bg-gray-100 px-4 py-3">Macrosettore</th>
              <th className="text-left bg-gray-100 px-4 py-3">Rischio</th>
              <th className="text-left bg-gray-100 px-4 py-3">{isArchivio ? 'Dismessa il' : 'Contratto'}</th>
              <th className="bg-gray-100 rounded-r-xl px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((c) => (
              <tr key={c.id} className={`border-b border-gray-50 hover:bg-gray-50/60 transition-colors cursor-pointer ${isArchivio ? 'opacity-80' : ''}`} data-testid={`azienda-row-${c.id}`} onClick={() => navigate(`/aziende/${encodeURIComponent(c.id)}`)}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-gray-800">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate max-w-md">{c.address}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-mono">{c.piva || '—'}</div>
                  {c.codiceFiscale && c.codiceFiscale !== c.piva && (
                    <div className="font-mono text-slate-500">{c.codiceFiscale}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-700 max-w-[240px]">
                  <div className="truncate" title={c.macrosettore || ''}>{c.macrosettore || '—'}</div>
                </td>
                <td className="px-4 py-3">
                  {c.rischio ? (
                    <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-bold ${rischioClass(c.rischio)}`}>{c.rischio}</span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3">
                  {isArchivio
                    ? <span className="text-xs text-slate-700">{fmtDate(c.dismessoAt) || '—'}</span>
                    : (c.contratto && c.contratto !== '-')
                      ? <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold bg-[#e0e7ff] text-[#023F80]">{c.contratto}</span>
                      : <span className="text-slate-300 text-xs">—</span>}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => navigate(`/aziende/${encodeURIComponent(c.id)}`)} title="Apri" className="p-1.5 rounded-md hover:bg-gray-100"><Eye size={15} className="text-gray-600" /></button>
                    {user?.role !== 'client' && !isArchivio && (
                      <>
                        <button onClick={() => crud.onEdit(c)} title="Modifica" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><Pencil size={14} className="text-gray-600" /></button>
                        <button onClick={() => setDismissTarget(c)} title="Dismetti" data-testid={`azienda-dismiss-${c.id}`} className="p-1.5 rounded-md hover:bg-red-50"><Archive size={14} className="text-red-600" /></button>
                      </>
                    )}
                    {user?.role !== 'client' && isArchivio && (
                      <button onClick={() => setReactivateTarget(c)} title="Riattiva" data-testid={`azienda-reactivate-${c.id}`} className="px-2 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center gap-1">
                        <RefreshCw size={12} /> RIATTIVA
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-10 text-center text-sm text-gray-400">{isArchivio ? 'Nessuna azienda in archivio' : 'Nessuna azienda trovata con i filtri attuali'}</div>}
      </div>

      <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Mostra</span>
          <Select value={pageSize} onValueChange={(v) => { setPageSize(v); setPage(1); }}>
            <SelectTrigger className="w-20 h-8 border-gray-200"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="10">10</SelectItem><SelectItem value="25">25</SelectItem><SelectItem value="50">50</SelectItem><SelectItem value="100">100</SelectItem></SelectContent>
          </Select>
          <span>elementi</span>
        </div>
        <div className="text-xs text-gray-500">{filtered.length > 0 ? (page - 1) * size + 1 : 0}–{Math.min(page * size, filtered.length)} di {filtered.length}</div>
        <div className="flex items-center gap-1">
          <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded-md text-xs font-semibold text-gray-600 disabled:opacity-40 hover:bg-gray-100">Precedente</button>
          <span className="text-xs text-gray-700 font-bold px-2">{page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded-md text-xs font-semibold text-gray-600 disabled:opacity-40 hover:bg-gray-100">Successivo</button>
        </div>
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(v) => { if (!v) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica azienda' : 'Nuova azienda'}
        description="Compila i dati anagrafici dell'azienda."
        fields={companyFields()}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
        submitLabel={crud.editing?.id ? 'Aggiorna' : 'Crea azienda'}
      />

      <AutoProfileDialog open={autoOpen} onOpenChange={setAutoOpen} companyIds={null} onApplied={reload} />
      <DismissCompanyDialog company={dismissTarget} open={!!dismissTarget} onOpenChange={(v) => !v && setDismissTarget(null)} onDone={reload} />
      <ReactivateCompanyDialog company={reactivateTarget} open={!!reactivateTarget} onOpenChange={(v) => !v && setReactivateTarget(null)} onDone={reload} />
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-[300px]">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

function rischioClass(v) {
  const r = (v || '').toUpperCase();
  if (r === 'ALTO') return 'bg-red-100 text-red-800';
  if (r === 'MEDIO') return 'bg-amber-100 text-amber-800';
  if (r === 'BASSO') return 'bg-emerald-100 text-emerald-800';
  return 'bg-slate-100 text-slate-700';
}

export default AziendePage;
